import dayjs from "dayjs";
import { Line, type LineConfig } from "@ant-design/plots";
import { AppCard } from "@lib/components/AppComponents";
import { Empty, Skeleton } from "antd";
import { useIntl } from "react-intl";
import { UseQueryProps } from "@lib/types";
import { XAICallLogsDashboardResponse } from "@lib/types/xAI";
import { useTheme } from "styled-components";
import { useMemo } from "react";

type Props = {
  query?: UseQueryProps<XAICallLogsDashboardResponse>;
  extra?: string | JSX.Element;
  navigate?: () => void;
  loading?: boolean;
  refetch?: () => void;
};

const TftSalesCallsDailyChart = (props: Props) => {
  const { messages } = useIntl();
  const { mode } = useTheme();

  const { query } = props;

  const { data, isLoading, isFetching } = query || {};
  const dailyCallCount = data?.data?.dailyCallCount || [];
  const chartData = useMemo(() => {
    if (!dailyCallCount || dailyCallCount.length === 0) return [];

    return dailyCallCount
      .map((item) => {
        const date = dayjs(item.call_date);
        return {
          date: date.format("DD/MM/YYYY"),
          value: item.call_count,
          dateValue: date.valueOf(),
        };
      })
      .sort((a, b) => a.dateValue - b.dateValue);
  }, [dailyCallCount]);

  const config: LineConfig = {
    theme: {
      type: mode,
    },
    data: chartData,

    yField: "value",
    xField: "date",
    axis: {
      x: {
        labelAutoRotate: false,
        title: messages["common.date"] as string,
        labelFontSize: 12,
        labelFormatter: (d) => `${d}`,
      },
      y: {
        title: messages["common.count"] as string,
        labelFontSize: 12,
        labelFormatter: (d) => `${d}`,
      },
    },

    tooltip: {
      items: [
        (datum, index, data, column) => ({
          value: `${column.y.value[index]}`,
        }),
      ],
    },

    autoFit: true,
    slider: {
      x: {
        labelFormatter: (d) => `${dayjs(d).format("DD/MM/YYYY")}`,
      },
      y: {
        labelFormatter: (d) => `${d}`,
      },
    },
  };

  return (
    <AppCard title={messages["common.dailyCallsChart"] as string}>
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {chartData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Line {...config} />
          )}
        </>
      )}
    </AppCard>
  );
};

export default TftSalesCallsDailyChart;
